import { forwardRef, type ReactNode } from 'react';
import { chakra, type HTMLChakraProps } from '@chakra-ui/react';

/**
 * Yaxle icon primitive.
 *
 * One stroke set, 24×24 grid, painted with `currentColor`. Color comes from
 * semantic tokens (`fg.muted`, `brand.solid`, `success.fg`, …) — never hex
 * inside a glyph. Add a name here instead of inlining an SVG or pulling
 * Tabler/Feather at the call site.
 */

export const ICON_NAMES = [
	'adjustments',
	'alert',
	'archive',
	'archiveOff',
	'arrowBackUp',
	'arrowDown',
	'arrowLeft',
	'arrowRight',
	'arrowUp',
	'arrowsSort',
	'ban',
	'bell',
	'bellOff',
	'buildingStore',
	'car',
	'category',
	'chartBar',
	'check',
	'checkBadge',
	'checkCircle',
	'checks',
	'circleOff',
	'circlePlus',
	'clipboardCheck',
	'collapse',
	'chevronDown',
	'chevronLeft',
	'chevronRight',
	'chevronUp',
	'creditCard',
	'dollar',
	'dotsVertical',
	'edit',
	'expand',
	'externalLink',
	'fileDollar',
	'fileExport',
	'fileInvoice',
	'fileText',
	'filter',
	'flag',
	'flagOff',
	'flask',
	'gripVertical',
	'headset',
	'history',
	'home',
	'info',
	'keyboard',
	'laptop',
	'listCheck',
	'logout',
	'mailForward',
	'mapPin',
	'megaphone',
	'message',
	'minus',
	'network',
	'notes',
	'package',
	'pencil',
	'plus',
	'plugConnected',
	'printer',
	'quote',
	'quoteOff',
	'receiptTax',
	'refresh',
	'restore',
	'search',
	'send',
	'server',
	'settings',
	'shieldCheck',
	'shieldLock',
	'shoppingCart',
	'sidebarCollapse',
	'sidebarExpand',
	'skipForward',
	'sparkles',
	'swap',
	'tag',
	'thumbDown',
	'trash',
	'trendingUp',
	'truck',
	'upload',
	'user',
	'users',
	'warehouse',
	'x',
	'xCircle',
] as const;

export type IconName = (typeof ICON_NAMES)[number];

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const ICON_SIZE: Record<IconSize, string> = {
	xs: '0.75rem',
	sm: '0.875rem',
	md: '1rem',
	lg: '1.25rem',
	xl: '1.5rem',
};

const GLYPHS: Record<IconName, ReactNode> = {
	adjustments: (
		<>
			<path d="M4 10a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
			<path d="M6 4v4" />
			<path d="M6 12v8" />
			<path d="M10 16a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
			<path d="M12 4v10" />
			<path d="M12 18v2" />
			<path d="M16 7a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
			<path d="M18 4v1" />
			<path d="M18 9v11" />
		</>
	),
	alert: (
		<>
			<path d="M12 3.6 21.4 20.2H2.6L12 3.6z" />
			<line x1="12" y1="10" x2="12" y2="14.2" />
			<circle cx="12" cy="17.2" r="0.7" fill="currentColor" stroke="none" />
		</>
	),
	archive: (
		<>
			<rect x="3" y="4" width="18" height="4" rx="1.5" />
			<path d="M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" />
			<line x1="10" y1="12" x2="14" y2="12" />
		</>
	),
	archiveOff: (
		<>
			<path d="M8 4h11a2 2 0 1 1 0 4h-7m-4 0h-3a2 2 0 0 1 -.826 -3.822" />
			<path d="M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 1.824 -1.18m.176 -3.82v-7" />
			<path d="M10 12h2" />
			<path d="M3 3l18 18" />
		</>
	),
	arrowBackUp: (
		<>
			<path d="M9 14l-4 -4l4 -4" />
			<path d="M5 10h11a4 4 0 1 1 0 8h-1" />
		</>
	),
	arrowDown: (
		<>
			<line x1="12" y1="5" x2="12" y2="19" />
			<polyline points="5 12 12 19 19 12" />
		</>
	),
	arrowLeft: (
		<>
			<line x1="19" y1="12" x2="5" y2="12" />
			<polyline points="12 5 5 12 12 19" />
		</>
	),
	arrowRight: (
		<>
			<line x1="5" y1="12" x2="19" y2="12" />
			<polyline points="12 5 19 12 12 19" />
		</>
	),
	arrowUp: (
		<>
			<line x1="12" y1="19" x2="12" y2="5" />
			<polyline points="5 12 12 5 19 12" />
		</>
	),
	arrowsSort: (
		<>
			<polyline points="8 9 8 4 5.5 6.5" />
			<polyline points="16 15 16 20 18.5 17.5" />
			<line x1="8" y1="4" x2="8" y2="20" />
			<line x1="16" y1="20" x2="16" y2="4" />
		</>
	),
	ban: (
		<>
			<path d="M3 12a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
			<path d="M5.7 5.7l12.6 12.6" />
		</>
	),
	bell: (
		<>
			<path d="M18 8A6 6 0 1 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
			<path d="M13.73 21a2 2 0 0 1-3.46 0" />
		</>
	),
	bellOff: (
		<>
			<path d="M18 8A6 6 0 1 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
			<path d="M13.73 21a2 2 0 0 1-3.46 0" />
			<path d="M3 3l18 18" />
		</>
	),
	buildingStore: (
		<>
			<path d="M3 7l2-4h14l2 4v1a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0z" />
			<line x1="5" y1="10.5" x2="5" y2="21" />
			<line x1="19" y1="10.5" x2="19" y2="21" />
			<line x1="3" y1="21" x2="21" y2="21" />
			<path d="M9 21v-4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v4" />
		</>
	),
	car: (
		<>
			<circle cx="7" cy="17" r="2" />
			<circle cx="17" cy="17" r="2" />
			<path d="M5 17H3v-6l2-5h9l4 5h1a2 2 0 0 1 2 2v4h-2" />
			<line x1="9" y1="17" x2="15" y2="17" />
			<line x1="3" y1="11" x2="18" y2="11" />
			<line x1="12" y1="11" x2="12" y2="6" />
		</>
	),
	category: (
		<>
			<rect x="4" y="4" width="6" height="6" rx="1" />
			<rect x="14" y="4" width="6" height="6" rx="1" />
			<rect x="4" y="14" width="6" height="6" rx="1" />
			<circle cx="17" cy="17" r="3" />
		</>
	),
	chartBar: (
		<>
			<rect x="3" y="12" width="6" height="8" rx="1" />
			<rect x="9" y="4" width="6" height="16" rx="1" />
			<rect x="15" y="8" width="6" height="12" rx="1" />
			<line x1="4" y1="20" x2="18" y2="20" />
		</>
	),
	check: <polyline points="20 6 9 17 4 12" />,
	checks: (
		<>
			<path d="M7 12l5 5l10 -10" />
			<path d="M2 12l5 5m5 -5l5 -5" />
		</>
	),
	checkBadge: (
		<>
			<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
			<polyline points="22 4 12 14.01 9 11.01" />
		</>
	),
	checkCircle: (
		<>
			<circle cx="12" cy="12" r="9" />
			<polyline points="8 12.2 10.8 15 16.2 9.4" />
		</>
	),
	circleOff: (
		<>
			<path d="M20.042 16.045a9 9 0 0 0 -12.087 -12.087m-2.318 1.677a9 9 0 1 0 12.725 12.73" />
			<path d="M3 3l18 18" />
		</>
	),
	clipboardCheck: (
		<>
			<path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
			<rect x="9" y="3" width="6" height="4" rx="2" />
			<polyline points="9 14 11 16 15 12" />
		</>
	),
	circlePlus: (
		<>
			<path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
			<path d="M9 12h6" />
			<path d="M12 9v6" />
		</>
	),
	chevronDown: <polyline points="6 9 12 15 18 9" />,
	chevronLeft: <polyline points="15 6 9 12 15 18" />,
	chevronRight: <polyline points="9 6 15 12 9 18" />,
	chevronUp: <polyline points="18 15 12 9 6 15" />,
	creditCard: (
		<>
			<rect x="3" y="5" width="18" height="14" rx="3" />
			<line x1="3" y1="10" x2="21" y2="10" />
			<line x1="7" y1="15" x2="7.01" y2="15" />
			<line x1="11" y1="15" x2="13" y2="15" />
		</>
	),
	collapse: (
		<>
			<polyline points="4 14 10 14 10 20" />
			<line x1="3" y1="21" x2="10" y2="14" />
			<polyline points="20 10 14 10 14 4" />
			<line x1="21" y1="3" x2="14" y2="10" />
		</>
	),
	dotsVertical: (
		<>
			<circle cx="12" cy="5" r="1" fill="currentColor" stroke="none" />
			<circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
			<circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" />
		</>
	),
	dollar: (
		<>
			<line x1="12" y1="1" x2="12" y2="23" />
			<path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
		</>
	),
	edit: (
		<>
			<path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1" />
			<path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415" />
			<path d="M16 5l3 3" />
		</>
	),
	expand: (
		<>
			<polyline points="16 4 20 4 20 8" />
			<line x1="14" y1="10" x2="20" y2="4" />
			<polyline points="8 20 4 20 4 16" />
			<line x1="10" y1="14" x2="4" y2="20" />
		</>
	),
	externalLink: (
		<>
			<path d="M14 4h6v6" />
			<path d="M20 4l-9 9" />
			<path d="M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4" />
		</>
	),
	fileDollar: (
		<>
			<path d="M14 3v4a1 1 0 0 0 1 1h4" />
			<path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
			<path d="M14 11h-2.5a1.5 1.5 0 1 0 0 3h1a1.5 1.5 0 1 1 0 3H10" />
			<line x1="12" y1="9" x2="12" y2="10" />
			<line x1="12" y1="17" x2="12" y2="18" />
		</>
	),
	fileExport: (
		<>
			<path d="M14 3v4a1 1 0 0 0 1 1h4" />
			<path d="M11.5 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v5" />
			<line x1="14" y1="18" x2="21" y2="18" />
			<polyline points="18 15 21 18 18 21" />
		</>
	),
	fileInvoice: (
		<>
			<path d="M14 3v4a1 1 0 0 0 1 1h4" />
			<path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
			<line x1="9" y1="7" x2="10" y2="7" />
			<line x1="9" y1="13" x2="15" y2="13" />
			<line x1="13" y1="17" x2="15" y2="17" />
		</>
	),
	fileText: (
		<>
			<path d="M14 3v4a1 1 0 0 0 1 1h4" />
			<path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
			<line x1="9" y1="9" x2="10" y2="9" />
			<line x1="9" y1="13" x2="15" y2="13" />
			<line x1="9" y1="17" x2="15" y2="17" />
		</>
	),
	filter: (
		<>
			<line x1="4" y1="7" x2="20" y2="7" />
			<line x1="7" y1="12" x2="17" y2="12" />
			<line x1="10" y1="17" x2="14" y2="17" />
		</>
	),
	flag: (
		<>
			<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
			<line x1="4" y1="22" x2="4" y2="15" />
		</>
	),
	flagOff: (
		<>
			<path d="M5 5v16" />
			<path d="M19 5v9" />
			<path d="M7.641 3.645a5 5 0 0 1 4.359 1.355a5 5 0 0 0 7 0" />
			<path d="M5 14a5 5 0 0 1 7 0a4.984 4.984 0 0 0 3.437 1.429m3.019 -.966c.19 -.14 .371 -.294 .544 -.463" />
			<path d="M3 3l18 18" />
		</>
	),
	flask: (
		<>
			<line x1="9" y1="3" x2="15" y2="3" />
			<path d="M14 3v7.342A6 6 0 0 1 15.318 21H8.683A6 6 0 0 1 10 10.34V3h4z" />
			<line x1="6.1" y1="15" x2="17.9" y2="15" />
		</>
	),
	gripVertical: (
		<>
			<circle cx="9" cy="6" r="1.1" fill="currentColor" stroke="none" />
			<circle cx="15" cy="6" r="1.1" fill="currentColor" stroke="none" />
			<circle cx="9" cy="12" r="1.1" fill="currentColor" stroke="none" />
			<circle cx="15" cy="12" r="1.1" fill="currentColor" stroke="none" />
			<circle cx="9" cy="18" r="1.1" fill="currentColor" stroke="none" />
			<circle cx="15" cy="18" r="1.1" fill="currentColor" stroke="none" />
		</>
	),
	headset: (
		<>
			<path d="M4 14v-3a8 8 0 1 1 16 0v3" />
			<path d="M18 19c0 1.657-2.686 3-6 3" />
			<rect x="4" y="12" width="5" height="7" rx="2" />
			<rect x="15" y="12" width="5" height="7" rx="2" />
		</>
	),
	history: (
		<>
			<polyline points="3.6 5.4 3.6 9.8 8 9.8" />
			<path d="M3.9 14a8.5 8.5 0 1 0 .5-6.5" />
			<polyline points="12 8 12 12.4 15 14.4" />
		</>
	),
	home: (
		<>
			<polyline points="3 12 12 3 21 12" />
			<path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
			<path d="M9 21v-6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v6" />
		</>
	),
	info: (
		<>
			<circle cx="12" cy="12" r="9" />
			<line x1="12" y1="11" x2="12" y2="16.2" />
			<circle cx="12" cy="8" r="0.7" fill="currentColor" stroke="none" />
		</>
	),
	keyboard: (
		<>
			<rect x="2" y="6" width="20" height="12" rx="2" />
			<line x1="6" y1="10" x2="6.01" y2="10" />
			<line x1="10" y1="10" x2="10.01" y2="10" />
			<line x1="14" y1="10" x2="14.01" y2="10" />
			<line x1="18" y1="10" x2="18.01" y2="10" />
			<line x1="6" y1="14" x2="6.01" y2="14" />
			<line x1="18" y1="14" x2="18.01" y2="14" />
			<line x1="10" y1="14" x2="14" y2="14" />
		</>
	),
	laptop: (
		<>
			<rect x="4" y="5" width="16" height="10" rx="1.5" />
			<line x1="4" y1="15" x2="20" y2="15" />
			<line x1="2" y1="19" x2="22" y2="19" />
		</>
	),
	listCheck: (
		<>
			<polyline points="3.5 5.5 5 7 7.5 4.5" />
			<polyline points="3.5 11.5 5 13 7.5 10.5" />
			<polyline points="3.5 17.5 5 19 7.5 16.5" />
			<line x1="11" y1="6" x2="20" y2="6" />
			<line x1="11" y1="12" x2="20" y2="12" />
			<line x1="11" y1="18" x2="20" y2="18" />
		</>
	),
	logout: (
		<>
			<path d="M14 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-2" />
			<line x1="20" y1="12" x2="9" y2="12" />
			<polyline points="16 8 20 12 16 16" />
		</>
	),
	mailForward: (
		<>
			<path d="M12 18H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v7.5" />
			<polyline points="3 6 12 12 21 6" />
			<line x1="15" y1="18" x2="21" y2="18" />
			<polyline points="18 15 21 18 18 21" />
		</>
	),
	mapPin: (
		<>
			<path d="M12 21s-6.5-5.686-6.5-10.5a6.5 6.5 0 1 1 13 0C18.5 15.314 12 21 12 21z" />
			<circle cx="12" cy="10.5" r="2.4" />
		</>
	),
	megaphone: (
		<>
			<path d="M18 8a3 3 0 0 1 0 6" />
			<path d="M10 8v11a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-5" />
			<path d="M12 8h0l4.524-3.77A.9.9 0 0 1 18 4.922v12.156a.9.9 0 0 1-1.476.692L12 14H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1h8" />
		</>
	),
	message: <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />,
	minus: <line x1="5" y1="12" x2="19" y2="12" />,
	network: (
		<>
			<circle cx="12" cy="6" r="2.2" />
			<circle cx="5" cy="18" r="2.2" />
			<circle cx="19" cy="18" r="2.2" />
			<line x1="12" y1="8.2" x2="6.6" y2="16.2" />
			<line x1="12" y1="8.2" x2="17.4" y2="16.2" />
		</>
	),
	notes: (
		<>
			<path d="M5 3h10l4 4v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
			<line x1="9" y1="12" x2="15" y2="12" />
			<line x1="9" y1="16" x2="15" y2="16" />
			<line x1="9" y1="8" x2="11" y2="8" />
		</>
	),
	package: (
		<>
			<path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z" />
			<polyline points="4 7.5 12 12 20 7.5" />
			<line x1="12" y1="12" x2="12" y2="21" />
			<line x1="16" y1="5.25" x2="8" y2="9.75" />
		</>
	),
	pencil: (
		<>
			<path d="M4 20l1-4L16.5 4.5a2.121 2.121 0 0 1 3 3L8 19l-4 1z" />
			<line x1="13.5" y1="7.5" x2="16.5" y2="10.5" />
		</>
	),
	plus: (
		<>
			<line x1="12" y1="5" x2="12" y2="19" />
			<line x1="5" y1="12" x2="19" y2="12" />
		</>
	),
	plugConnected: (
		<>
			<path d="M7 12l5 5-1.5 1.5a3.536 3.536 0 1 1-5-5L7 12z" />
			<path d="M17 12l-5-5 1.5-1.5a3.536 3.536 0 1 1 5 5L17 12z" />
			<line x1="3" y1="21" x2="5.5" y2="18.5" />
			<line x1="18.5" y1="5.5" x2="21" y2="3" />
			<line x1="10" y1="11" x2="8" y2="13" />
			<line x1="13" y1="14" x2="11" y2="16" />
		</>
	),
	printer: (
		<>
			<polyline points="7 8 7 3 17 3 17 8" />
			<path d="M7 17H5a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
			<rect x="7" y="14" width="10" height="7" rx="0.5" />
		</>
	),
	quote: (
		<>
			<path d="M10 11h-4a1 1 0 0 1 -1 -1v-3a1 1 0 0 1 1 -1h3a1 1 0 0 1 1 1v6c0 2.667 -1.333 4.333 -4 5" />
			<path d="M19 11h-4a1 1 0 0 1 -1 -1v-3a1 1 0 0 1 1 -1h3a1 1 0 0 1 1 1v6c0 2.667 -1.333 4.333 -4 5" />
		</>
	),
	quoteOff: (
		<>
			<path d="M10 11h-4a1 1 0 0 1 -1 -1v-3a1 1 0 0 1 1 -1m4 4v3c0 2.667 -1.333 4.333 -4 5" />
			<path d="M19 11h-4m-1 -1v-3a1 1 0 0 1 1 -1h3a1 1 0 0 1 1 1v6c0 .66 -.082 1.26 -.245 1.798m-1.653 2.29c-.571 .4 -1.272 .704 -2.102 .912" />
			<path d="M3 3l18 18" />
		</>
	),
	receiptTax: (
		<>
			<path d="M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16l-3-2-2 2-2-2-2 2-2-2-3 2z" />
			<line x1="9" y1="14" x2="15" y2="8" />
			<circle cx="9.5" cy="8.5" r="0.6" fill="currentColor" stroke="none" />
			<circle cx="14.5" cy="13.5" r="0.6" fill="currentColor" stroke="none" />
		</>
	),
	refresh: (
		<>
			<path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
			<path d="M3 3v5h5" />
			<path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
			<path d="M16 16h5v5" />
		</>
	),
	restore: (
		<>
			<path d="M3.06 13a9 9 0 1 0 .49 -4.087" />
			<path d="M3 4.001v5h5" />
			<path d="M11 12a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
		</>
	),
	search: (
		<>
			<circle cx="11" cy="11" r="6.5" />
			<line x1="16.2" y1="16.2" x2="20" y2="20" />
		</>
	),
	send: (
		<>
			<path d="M10 14l11 -11" />
			<path d="M21 3l-6.5 18a.55 .55 0 0 1 -1 0l-3.5 -7l-7 -3.5a.55 .55 0 0 1 0 -1l18 -6.5" />
		</>
	),
	server: (
		<>
			<rect x="3" y="3" width="18" height="8" rx="1.5" />
			<rect x="3" y="13" width="18" height="8" rx="1.5" />
			<circle cx="7" cy="7" r="0.8" fill="currentColor" stroke="none" />
			<circle cx="7" cy="17" r="0.8" fill="currentColor" stroke="none" />
		</>
	),
	settings: (
		<>
			<path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37 1 .608 2.296.07 2.572-1.065z" />
			<circle cx="12" cy="12" r="3" />
		</>
	),
	shieldCheck: (
		<>
			<path d="M12 3a12 12 0 0 0 8.5 3a12 12 0 0 1 -8.5 15a12 12 0 0 1 -8.5 -15a12 12 0 0 0 8.5 -3" />
			<path d="M9 12l2 2l4 -4" />
		</>
	),
	shieldLock: (
		<>
			<path d="M12 3a12 12 0 0 0 8.5 3a12 12 0 0 1 -8.5 15a12 12 0 0 1 -8.5 -15a12 12 0 0 0 8.5 -3" />
			<circle cx="12" cy="11" r="1" />
			<line x1="12" y1="12" x2="12" y2="14.5" />
		</>
	),
	shoppingCart: (
		<>
			<circle cx="9" cy="21" r="1" />
			<circle cx="20" cy="21" r="1" />
			<path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
		</>
	),
	sidebarCollapse: (
		<>
			<rect x="4" y="4" width="16" height="16" rx="2" />
			<path d="M9 4v16" />
			<path d="M15 10l-2 2l2 2" />
		</>
	),
	sidebarExpand: (
		<>
			<rect x="4" y="4" width="16" height="16" rx="2" />
			<path d="M9 4v16" />
			<path d="M14 10l2 2l-2 2" />
		</>
	),
	skipForward: (
		<>
			<path d="M4 5v14l12 -7l-12 -7" />
			<path d="M20 5l0 14" />
		</>
	),
	sparkles: (
		<>
			<path d="M9 18a6 6 0 0 1 6-6 6 6 0 0 1-6-6 6 6 0 0 1-6 6 6 6 0 0 1 6 6z" />
			<path d="M16 6a2 2 0 0 1 2 2 2 2 0 0 1 2-2 2 2 0 0 1-2-2 2 2 0 0 1-2 2z" />
			<path d="M16 18a2 2 0 0 1 2 2 2 2 0 0 1 2-2 2 2 0 0 1-2-2 2 2 0 0 1-2 2z" />
		</>
	),
	swap: (
		<>
			<path d="M4 8h13l-3.5-3.5" />
			<path d="M20 16H7l3.5 3.5" />
		</>
	),
	tag: (
		<>
			<path d="M6.5 7.5a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
			<path d="M3 6v5.172a2 2 0 0 0 .586 1.414l7.71 7.71a2.41 2.41 0 0 0 3.408 0l5.592 -5.592a2.41 2.41 0 0 0 0 -3.408l-7.71 -7.71a2 2 0 0 0 -1.414 -.586h-5.172a3 3 0 0 0 -3 3" />
		</>
	),
	thumbDown: (
		<>
			<path d="M7 13v-8a1 1 0 0 0 -1 -1h-2a1 1 0 0 0 -1 1v7a1 1 0 0 0 1 1h3a4 4 0 0 1 4 4v1a2 2 0 0 0 4 0v-5h3a2 2 0 0 0 2 -2l-1 -5a2 3 0 0 0 -2 -2h-7a3 3 0 0 0 -3 3" />
		</>
	),
	trash: (
		<>
			<polyline points="4 7 20 7" />
			<path d="M6 7l1 13a1.5 1.5 0 0 0 1.5 1.4h7A1.5 1.5 0 0 0 17 20l1-13" />
			<path d="M9 7V4.5A1.5 1.5 0 0 1 10.5 3h3A1.5 1.5 0 0 1 15 4.5V7" />
			<line x1="10" y1="11" x2="10" y2="17" />
			<line x1="14" y1="11" x2="14" y2="17" />
		</>
	),
	trendingUp: (
		<>
			<polyline points="3 17 9 11 13 15 21 7" />
			<polyline points="14 7 21 7 21 14" />
		</>
	),
	truck: (
		<>
			<circle cx="7" cy="17" r="2" />
			<circle cx="17" cy="17" r="2" />
			<path d="M5 17H3v-4M2 5h11v12M9 17h6M19 17h2v-6h-8M13 6h5l3 5" />
			<line x1="3" y1="9" x2="7" y2="9" />
		</>
	),
	upload: (
		<>
			<path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
			<polyline points="7 9 12 4 17 9" />
			<line x1="12" y1="4" x2="12" y2="15" />
		</>
	),
	user: (
		<>
			<circle cx="12" cy="7" r="4" />
			<path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
		</>
	),
	users: (
		<>
			<circle cx="9" cy="8" r="3.2" />
			<path d="M2.5 20v-1.2A4.3 4.3 0 0 1 6.8 14.5h4.4a4.3 4.3 0 0 1 4.3 4.3V20" />
			<path d="M16.5 5.2a3.2 3.2 0 0 1 0 5.6" />
			<path d="M18.4 14.8A4 4 0 0 1 21.5 18.7V20" />
		</>
	),
	warehouse: (
		<>
			<path d="M3 21V8l9-4 9 4v13" />
			<path d="M13 13h4v8H7v-6h6" />
			<path d="M13 21v-9a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v3" />
		</>
	),
	x: (
		<>
			<line x1="6" y1="6" x2="18" y2="18" />
			<line x1="18" y1="6" x2="6" y2="18" />
		</>
	),
	xCircle: (
		<>
			<circle cx="12" cy="12" r="9" />
			<line x1="9" y1="9" x2="15" y2="15" />
			<line x1="15" y1="9" x2="9" y2="15" />
		</>
	),
};

export interface IconProps extends Omit<HTMLChakraProps<'svg'>, 'as'> {
	name: IconName;
	/** Named scale, or any CSS length / Chakra size token. */
	size?: IconSize | string | number;
}

export const Icon = forwardRef<SVGSVGElement, IconProps>(
	({ name, size = 'md', strokeWidth = 2, ...rest }, ref) => {
		const box = typeof size === 'string' && size in ICON_SIZE ? ICON_SIZE[size as IconSize] : size;
		return (
			<chakra.svg
				ref={ref}
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth={strokeWidth}
				strokeLinecap="round"
				strokeLinejoin="round"
				w={box}
				h={box}
				flexShrink={0}
				display="inline-block"
				verticalAlign="middle"
				aria-hidden
				{...rest}
			>
				{GLYPHS[name]}
			</chakra.svg>
		);
	},
);
Icon.displayName = 'Icon';

export default Icon;
